/**
 * GuidedTour — Spotlight-style onboarding tour
 * ─────────────────────────────────────────────
 * Shows once per browser (localStorage key `ff_tour_v1`).
 * Each step highlights a DOM element by id via a radial-mask overlay.
 * Fully keyboard-navigable and aria-live enabled.
 */

import React, { useEffect, useState, useCallback, CSSProperties } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, Sparkles } from 'lucide-react';

interface TourStep {
  targetId: string;         // id of the element to spotlight
  title: string;
  body: string;
  tooltipSide: 'top' | 'bottom' | 'left' | 'right';
}

const STEPS: TourStep[] = [
  {
    targetId: 'tour-logo',
    title: 'Welcome to FocusFlow ✨',
    body: 'Your cinematic productivity space. Let\'s take a 30-second tour.',
    tooltipSide: 'right',
  },
  {
    targetId: 'tour-nav-dashboard',
    title: 'Dashboard',
    body: 'Your command centre — live stats, forest growth, and study streaks.',
    tooltipSide: 'right',
  },
  {
    targetId: 'tour-nav-timer',
    title: 'Focus Timer',
    body: 'Enter immersive mode for deep Pomodoro sessions with ambient sounds.',
    tooltipSide: 'right',
  },
  {
    targetId: 'tour-nav-tasks',
    title: 'Planner',
    body: 'Drag tasks across the Kanban board or plan with natural language commands.',
    tooltipSide: 'right',
  },
  {
    targetId: 'tour-fab',
    title: 'Quick-Add FAB',
    body: 'Tap the glowing button anytime to create a task in one shot.',
    tooltipSide: 'top',
  },
];

const LS_KEY = 'ff_tour_v1';
const PAD = 18; // spotlight padding around element

const GuidedTour: React.FC = () => {
  const [step, setStep] = useState(0);
  const [active, setActive] = useState(false);
  const [spotRect, setSpotRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (localStorage.getItem(LS_KEY)) return;
    // Small delay so layout is painted
    const t = setTimeout(() => setActive(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const measureTarget = useCallback((idx: number) => {
    const el = document.getElementById(STEPS[idx]?.targetId);
    if (!el) return;
    setSpotRect(el.getBoundingClientRect());
  }, []);

  useEffect(() => {
    if (active) measureTarget(step);
  }, [active, step, measureTarget]);

  // Remeasure on resize
  useEffect(() => {
    if (!active) return;
    const handler = () => measureTarget(step);
    window.addEventListener('resize', handler, { passive: true });
    return () => window.removeEventListener('resize', handler);
  }, [active, step, measureTarget]);

  const finish = useCallback(() => {
    localStorage.setItem(LS_KEY, '1');
    setActive(false);
  }, []);

  const next = useCallback(() => {
    if (step >= STEPS.length - 1) {
      finish();
    } else {
      setStep((s) => s + 1);
    }
  }, [step, finish]);

  // Keyboard nav
  useEffect(() => {
    if (!active) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
      if (e.key === 'ArrowRight' || e.key === 'Enter') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [active, finish, next]);

  if (!active || !spotRect) return null;

  // Spotlight mask CSS variables
  const spotX = spotRect.left + spotRect.width / 2;
  const spotY = spotRect.top  + spotRect.height / 2;
  const spotW = spotRect.width  + PAD * 2;
  const spotH = spotRect.height + PAD * 2;

  // Tooltip position
  const currentStep = STEPS[step];
  const side = currentStep.tooltipSide;
  let tipLeft = spotRect.left;
  let tipTop = spotRect.bottom + 16;
  if (side === 'right') {
    tipLeft = spotRect.right + 18;
    tipTop  = spotRect.top + spotRect.height / 2 - 80;
  } else if (side === 'top') {
    tipLeft = spotRect.left + spotRect.width / 2 - 150;
    tipTop  = spotRect.top - 172;
  } else if (side === 'left') {
    tipLeft = spotRect.left - 318;
    tipTop  = spotRect.top + spotRect.height / 2 - 80;
  }
  // Clamp within viewport
  tipLeft = Math.max(12, Math.min(tipLeft, window.innerWidth  - 312));
  tipTop  = Math.max(12, Math.min(tipTop,  window.innerHeight - 220));

  return (
    <AnimatePresence>
      <motion.div
        key="tour-root"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        style={{ position: 'fixed', inset: 0, zIndex: 9000, pointerEvents: 'none' }}
        aria-live="polite"
      >
        {/* Spotlight overlay — plain div, CSS vars set via style (no TS clash) */}
        <div
          className="tour-spotlight-overlay"
          style={{
            '--spot-x': `${spotX}px`,
            '--spot-y': `${spotY}px`,
            '--spot-w': `${spotW}px`,
            '--spot-h': `${spotH}px`,
            transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1)',
          } as CSSProperties}
        />

        {/* Spotlight border ring — animates left/top/width/height via motion */}
        <motion.div
          aria-hidden="true"
          animate={{
            left: spotRect.left - PAD,
            top:  spotRect.top  - PAD,
            width: spotW,
            height: spotH,
          }}
          transition={{ type: 'spring', stiffness: 220, damping: 26 }}
          style={{
            position: 'fixed',
            borderRadius: 16,
            border: '2px solid rgba(124,58,237,0.7)',
            boxShadow: '0 0 0 4px rgba(124,58,237,0.12), 0 0 24px rgba(124,58,237,0.3)',
            pointerEvents: 'none',
            animation: 'rimPulse 2s ease-in-out infinite',
          }}
        />

        {/* Tooltip card */}
        <motion.div
          key={`tooltip-${step}`}
          role="dialog"
          aria-label={`Tour step ${step + 1} of ${STEPS.length}: ${currentStep.title}`}
          initial={{ opacity: 0, scale: 0.92, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 8 }}
          transition={{ type: 'spring', stiffness: 340, damping: 28 }}
          style={{
            position: 'fixed',
            left: tipLeft,
            top: tipTop,
            width: 298,
            pointerEvents: 'auto',
            borderRadius: 20,
            padding: '20px 22px',
            background: 'rgba(9, 14, 28, 0.88)',
            backdropFilter: 'blur(22px)',
            WebkitBackdropFilter: 'blur(22px)',
            border: '1px solid rgba(124,58,237,0.28)',
            boxShadow: '0 8px 40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Sparkles size={15} style={{ color: '#7C3AED', flexShrink: 0 }} />
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#A78BFA' }}>
                {step + 1} / {STEPS.length}
              </span>
            </div>
            <button
              aria-label="Skip tour"
              onClick={finish}
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 8,
                color: '#94A3B8',
                cursor: 'pointer',
                padding: '2px 6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={13} />
            </button>
          </div>

          <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 800, color: '#F1F5F9', lineHeight: 1.3 }}>
            {currentStep.title}
          </h3>
          <p style={{ margin: 0, fontSize: 13, color: '#94A3B8', lineHeight: 1.55 }}>
            {currentStep.body}
          </p>

          {/* Progress dots */}
          <div style={{ display: 'flex', gap: 5, marginTop: 16, marginBottom: 14 }}>
            {STEPS.map((_, i) => (
              <div
                key={i}
                style={{
                  width: i === step ? 20 : 6,
                  height: 6,
                  borderRadius: 3,
                  background: i === step ? '#7C3AED' : 'rgba(255,255,255,0.12)',
                  transition: 'width 0.3s ease, background 0.3s ease',
                }}
              />
            ))}
          </div>

          {/* Controls */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              aria-label="Skip tour"
              onClick={finish}
              style={{
                flex: 1,
                padding: '8px 0',
                borderRadius: 10,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#64748B',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Skip
            </button>
            <button
              aria-label={step >= STEPS.length - 1 ? 'Finish tour' : 'Next step'}
              onClick={next}
              style={{
                flex: 2,
                padding: '8px 0',
                borderRadius: 10,
                background: 'linear-gradient(135deg, #7C3AED, #6D28D9)',
                border: 'none',
                color: '#fff',
                fontSize: 12,
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                boxShadow: '0 4px 16px rgba(124,58,237,0.4)',
              }}
            >
              {step >= STEPS.length - 1 ? 'Finish 🎉' : 'Next'}
              {step < STEPS.length - 1 && <ChevronRight size={14} />}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GuidedTour;
